import React, { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { getDownloadURL, ref, uploadBytesResumable } from 'firebase/storage';
import { useData } from '../../context/DataContext';
import { useAuth } from '../../context/AuthContext';
import { storage } from '../../firebase';
import { addSubmissionFs } from '../../utils/firestoreStore';
import { formatDate, formatDateTime, timestampValue } from '../../utils/display';
import { EmptyState, InlineAlert, LoadingState, PageHeader, StatusBadge, Tabs } from '../../components/ui/Primitives';

const tabs = [
  { value: 'todo', label: 'To do' },
  { value: 'submitted', label: 'Submitted' },
  { value: 'graded', label: 'Graded' }
];

const submissionFor = (assignmentId, submissions, studentId) => submissions
  .filter(item => item.assignmentId === assignmentId && item.studentId === studentId)
  .sort((a, b) => timestampValue(b.createdAt || b.submittedAt) - timestampValue(a.createdAt || a.submittedAt))[0];

const assignmentState = (assignment, submission) => {
  if (submission?.marks !== null && submission?.marks !== undefined && submission?.marks !== '') return { key: 'graded', label: 'Graded', tone: 'success' };
  if (submission) return { key: 'submitted', label: 'Submitted', tone: 'info' };
  const due = timestampValue(assignment.dueDate && assignment.dueTime ? `${assignment.dueDate}T${assignment.dueTime}` : assignment.dueDate);
  return due && due < Date.now()
    ? { key: 'todo', label: 'Overdue', tone: 'danger' }
    : { key: 'todo', label: 'To do', tone: 'warning' };
};

const attachmentDetails = attachment => typeof attachment === 'string'
  ? { name: attachment, url: attachment.startsWith('http') ? attachment : '' }
  : { name: attachment?.name || attachment?.title || 'Attachment', url: attachment?.url || attachment?.downloadUrl || '' };

function CopyList({ title, value }) {
  const values = Array.isArray(value) ? value : value ? [value] : [];
  if (!values.length) return null;
  return <section className="assignment-detail-section"><h2>{title}</h2>{values.length === 1 ? <p>{values[0]}</p> : <ul>{values.map((item, index) => <li key={`${item}-${index}`}>{item}</li>)}</ul>}</section>;
}

export default function StudentAssignments() {
  const { assignments = [], submissions = [], loading = false } = useData() || {};
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState('todo');
  const [response, setResponse] = useState('');
  const [files, setFiles] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const selectedId = searchParams.get('assignment') || '';

  const scopedAssignments = useMemo(() => assignments.filter(assignment => {
    if (assignment.courseId && user?.courseId) return assignment.courseId === user.courseId;
    if (assignment.intake && user?.intake) return assignment.intake === user.intake;
    return !assignment.courseId && !assignment.intake;
  }), [assignments, user?.courseId, user?.intake]);

  const records = useMemo(() => scopedAssignments.map(assignment => {
    const submission = submissionFor(assignment.id, submissions, user?.id);
    return { assignment, submission, status: assignmentState(assignment, submission) };
  }).sort((a, b) => timestampValue(a.assignment.dueDate) - timestampValue(b.assignment.dueDate)), [scopedAssignments, submissions, user?.id]);
  const visible = records.filter(item => item.status.key === activeTab);
  const selected = records.find(item => item.assignment.id === selectedId) || null;
  const tabItems = tabs.map(tab => ({ ...tab, count: records.filter(item => item.status.key === tab.value).length }));

  const selectAssignment = id => {
    setSearchParams({ assignment: id });
    setResponse(''); setFiles([]); setError(''); setNotice(''); setShowForm(false);
  };
  const closeDetail = () => {
    setSearchParams({});
    setResponse(''); setFiles([]); setError(''); setNotice(''); setShowForm(false);
  };
  const selectFiles = event => {
    const next = Array.from(event.target.files || []);
    const tooLarge = next.find(file => file.size > 10 * 1024 * 1024);
    if (tooLarge) { setError('Each file must be 10 MB or less.'); return; }
    setError('');
    setFiles(next);
  };
  const submit = async () => {
    if (!selected || (!response.trim() && !files.length)) { setError('Add a written response or at least one attachment.'); return; }
    if (!user?.id) { setError('Your session is unavailable. Refresh the page and try again.'); return; }
    setSubmitting(true); setError(''); setNotice('');
    try {
      const uploadedFiles = [];
      for (const file of files) {
        const storagePath = `submissions/${user.id}/${selected.assignment.id}/${Date.now()}-${file.name}`;
        const storageRef = ref(storage, storagePath);
        await uploadBytesResumable(storageRef, file);
        uploadedFiles.push({ name: file.name, url: await getDownloadURL(storageRef), size: file.size, type: file.type, storagePath });
      }
      await addSubmissionFs({
        assignmentId: selected.assignment.id,
        studentId: user.id,
        studentName: `${user.firstName || ''} ${user.lastName || ''}`.trim(),
        studentEmail: user.email || '',
        courseId: selected.assignment.courseId || user.courseId || null,
        intake: selected.assignment.intake || user.intake || null,
        teacherId: selected.assignment.teacherId || selected.assignment.createdBy || null,
        submittedAt: new Date().toISOString(),
        content: response.trim(), files: uploadedFiles, status: 'submitted', marks: null
      });
      setResponse(''); setFiles([]); setShowForm(false);
      setNotice('Your assignment was submitted successfully.');
    } catch (submitError) {
      console.error('Failed to submit assignment', submitError);
      setError('The submission could not be uploaded. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <LoadingState label="Loading assignments…" />;

  if (selected) {
    const { assignment, submission, status } = selected;
    const attachments = (assignment.attachments || assignment.details?.resources || []).map(attachmentDetails);
    const canSubmit = status.key === 'todo' || showForm;
    return (
      <div className="assignment-detail">
        <button type="button" className="assignment-back" onClick={closeDetail}>← Back to assignments</button>
        <header className="assignment-detail-header">
          <p className="ui-eyebrow">{assignment.module || 'Course assignment'}</p>
          <h1>{assignment.title || 'Assignment'}</h1>
          <div className="assignment-detail-meta">
            <span>Due {formatDate(assignment.dueDate)}{assignment.dueTime ? ` at ${assignment.dueTime}` : ''}</span>
            <StatusBadge tone={status.tone}>{status.label}</StatusBadge>
          </div>
        </header>
        {notice && <InlineAlert tone="success">{notice}</InlineAlert>}
        {error && <InlineAlert tone="danger">{error}</InlineAlert>}
        <CopyList title="Instructions" value={assignment.details?.instructions || assignment.instructions || assignment.details?.overview || assignment.description} />
        {attachments.length > 0 && <section className="assignment-detail-section"><h2>Attachments</h2><div className="assignment-files">{attachments.map((file, index) => <div className="assignment-file" key={`${file.name}-${index}`}><span>{file.name}</span>{file.url ? <a className="ui-text-action" href={file.url} target="_blank" rel="noreferrer">Open →</a> : <span className="ui-muted">Provided in course material</span>}</div>)}</div></section>}
        {submission && !showForm && (
          <section className="assignment-detail-section">
            <h2>Your submission</h2>
            <p>Submitted {formatDateTime(submission.submittedAt || submission.createdAt)}</p>
            {submission.content && <p>{submission.content}</p>}
            {(submission.files || []).map(file => <div className="assignment-file" key={file.storagePath || file.name}><span>{file.name}</span>{file.url && <a className="ui-text-action" href={file.url} target="_blank" rel="noreferrer">Open →</a>}</div>)}
            {status.key !== 'graded' && <button type="button" className="btn btn-outline" onClick={() => setShowForm(true)}>Submit a revision</button>}
          </section>
        )}
        {status.key === 'graded' && <section className="assignment-detail-section"><h2>Result</h2><div className="assignment-result"><strong>{submission.marks}</strong><p>{submission.feedback || 'No written feedback was provided.'}</p></div></section>}
        {canSubmit && (
          <section className="assignment-detail-section">
            <h2>{submission ? 'Submit a revision' : 'Your submission'}</h2>
            <div className="assignment-submission-form">
              <label className="ui-field"><span>Written response</span><textarea value={response} onChange={event => setResponse(event.target.value)} placeholder="Write your response here" /></label>
              <label className="assignment-upload"><strong>Attach files</strong><span> PDF, Word, or image files up to 10 MB each</span><input type="file" multiple accept=".pdf,.doc,.docx,image/*" onChange={selectFiles} /></label>
              {files.length > 0 && <div className="assignment-files">{files.map(file => <div className="assignment-file" key={file.name}><span>{file.name}</span><span className="ui-muted">{Math.ceil(file.size / 1024)} KB</span></div>)}</div>}
              <div><button type="button" className="btn btn-primary" disabled={submitting || (!response.trim() && !files.length)} onClick={submit}>{submitting ? 'Submitting…' : 'Submit assignment'}</button>{showForm && <button type="button" className="btn btn-ghost" onClick={() => setShowForm(false)}>Cancel revision</button>}</div>
            </div>
          </section>
        )}
      </div>
    );
  }

  return (
    <div>
      <PageHeader eyebrow="Coursework" title="Assignments" description="Keep track of your coursework and submissions." />
      <Tabs items={tabItems} active={activeTab} onChange={setActiveTab} ariaLabel="Assignment status" />
      {visible.length ? <div className="academic-list">{visible.map(({ assignment, status }) => (
        <button type="button" className="academic-row academic-row-button" key={assignment.id} onClick={() => selectAssignment(assignment.id)}>
          <span><strong>{assignment.title || 'Assignment'}</strong><small>{assignment.module || 'Course assignment'}</small></span>
          <time>{formatDate(assignment.dueDate)}</time>
          <StatusBadge tone={status.tone}>{status.label}</StatusBadge>
          <span className="ui-text-action">View →</span>
        </button>
      ))}</div> : <EmptyState title={`No ${tabs.find(tab => tab.value === activeTab)?.label.toLowerCase()} assignments`} description="Assignments will move here as their status changes." />}
    </div>
  );
}
